import type { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { ChevronLeft } from "lucide-react"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { t } from "@/lib/i18n"

/**
 * The shell every screen under /manage/location that is not the overview wears:
 * the way back to the branch, then the section's own title.
 *
 * The back link exists here and not in `PageHeader` because these screens are
 * reached from a list — the branch overview — rather than from a bar that stays
 * on screen. Without it a manager who opened "Propinas" has no marked way back
 * to the branch, only the sidebar entry that reads as a fresh navigation.
 *
 * `max-w-2xl` matches the schedule screen this pattern grew out of. Operate
 * surfaces use `MainContainer size="md"` for their gutter and their measure, but
 * a single-record form is a focused surface: address fields stretched across a
 * 7xl container read as fields nobody finished filling.
 */
export function LocationSectionScreen({
  title,
  description,
  actions,
  children,
}: {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <StaffLayout>
      <MainContainer size="md">
        <div className="max-w-2xl space-y-4">
          <Link
            href="/manage/location"
            className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:min-h-9"
            data-testid="location-section-back"
          >
            <ChevronLeft className="size-4" />
            {t("ui.navigation.branch")}
          </Link>

          <PageHeader title={title} description={description} actions={actions} />

          {children}
        </div>
      </MainContainer>
    </StaffLayout>
  )
}
